import React, { useContext, useEffect, useState } from "react";
import ListingHeaderLayout from "../../layouts/ListingHeaderLayout";
import ThreeColumn from "../../core/layouts/ThreeColumn";
import {
  getConditionalContent,
  getFilterDataForQuestionnaireListing,
  getLocalizedMessage,
  getProductAndSubcategoryFromFilterData,
  getSelectedQuestionnaireTypes,
} from "../../UtilityFunctions/utils";
import { Text, Input, FilterOptions } from "react_reusable_components";
import {
  DownloadOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useIntl } from "react-intl";
import useResponsive from "../../core/hooks/useResponsive";
import { useNavigate } from "react-router-dom";
import Button from "../../components/Button";
import variables from "../../themes/base/styles/variables";
import Styles from "./QuestionnaireListingTopContainer.module.scss";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import { ProductCategoryContext } from "../../globalContext/productCategory/productCategoryProvider";
import ProductCategoryService from "../../services/product-category-service";
import { QUESTIONNAIRE_TYPE_FILTER_OPTIONS, userRole } from "../../constants";
import { QuestionnaireContext } from "../../globalContext/questionnaire/questionnaireProvider";
import { setQuestionnaireQuery } from "../../globalContext/questionnaire/questionnaireActions";
import { AuthContext } from "../../globalContext/auth/authProvider";

const questionnaireListingTopContainerStyle = {
  secondSectionMiddleSectionStyle: {
    display: variables.dFlex,
    gap: variables.gapSmaller,
    flexDirection: "row",
  },
  countStyle: {
    backgroundColor: "rgba(220, 0, 50, 0.12)",
    color: variables.primaryBg,
    height: variables.closeIcon,
    padding: variables.paddingXXSmall,
    display: variables.dFlex,
    justifyContent: variables.alignCenter,
    alignItems: variables.alignCenter,
    borderRadius: variables.countTagBorderRadius,
    fontSize: variables.fontSizeSmallest,
    fontWeight: variables.fontWeightBold,
    marginLeft: variables.marginSmall,
  },
};

export default function QuestionnaireListingTopContainer({
  query,
  setQuery,
  setFilter,
  downloadQuestionnaireReportsHandler,
}) {
  const intl = useIntl();
  const responsive = useResponsive();
  const navigate = useNavigate();
  const [authState] = useContext(AuthContext);
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const [productCategoryState, productCategoryDispatch] = useContext(
    ProductCategoryContext
  );
  const [questionnaireState, questionnaireDispatch] =
    useContext(QuestionnaireContext);
  const [open, setOpen] = useState(false);
  const [filtersCount, setFiltersCount] = useState(0);
  const questionnaireTypeFilterOptions = QUESTIONNAIRE_TYPE_FILTER_OPTIONS;

  useEffect(() => {
    ProductCategoryService.getProductCategoryList(
      productCategoryDispatch,
      {},
      intl,
      loaderDispatch
    );
  }, []);

  const filterOptions = getFilterDataForQuestionnaireListing(
    questionnaireTypeFilterOptions,
    productCategoryState?.productCategoryList?.result
  );

  const handleInputChange = (e) => {
    setQuery((prevQuery) => ({
      ...prevQuery,
      searchStr: e.target.value,
    }));
  };

  const onResetFilter = () => {
    setFiltersCount(0);
    setFilter({
      questionnaireType: [],
      productCategories: [],
      subCategories: [],
    });
    questionnaireDispatch(
      setQuestionnaireQuery({ ...questionnaireState?.query, prevFilters: [] })
    );
    setOpen(false);
  };

  const onApplyFilter = (data) => {
    const selectedQuestionnaireTypes = getSelectedQuestionnaireTypes(data?.[0]);
    const [productCategory, subCategory] =
      getProductAndSubcategoryFromFilterData(data?.[1]);

    setFilter(() => ({
      productCategories: productCategory,
      subCategories: subCategory,
      questionnaireType: selectedQuestionnaireTypes,
    }));
    setOpen(false);
    questionnaireDispatch(
      setQuestionnaireQuery({ ...questionnaireState?.query, prevFilters: data })
    );
  };

  useEffect(() => {
    let count = getCountValue(questionnaireState);

    setFiltersCount(count);
  }, [questionnaireState?.query?.prevFilters]);

  return (
    <ListingHeaderLayout
      firstSection={
        <Text className={Styles["heading-text"]}>
          {getLocalizedMessage(intl, "questionnaire.listHeading")}
        </Text>
      }
      secondSection={
        <ThreeColumn
          className={Styles["second-section-style"]}
          leftSectionStyle={{ flex: 1 }}
          leftSection={
            <Input
              className={`${Styles["input-style"]}
              ${Styles[!responsive?.isMd && "input-style-mobile"]}`}
              placeholder={getLocalizedMessage(
                intl,
                "questionnaire.searchPlaceholder"
              )}
              prefix={
                <SearchOutlined className={Styles["search-icon-style"]} />
              }
              onChange={handleInputChange}
              value={query?.searchStr}
            />
          }
          middleSectionStyle={
            questionnaireListingTopContainerStyle.secondSectionMiddleSectionStyle
          }
          middleSection={
            <>
              <FilterOptions
                defaultSelectedValue={questionnaireState?.query?.prevFilters}
                filterData={filterOptions}
                open={open}
                isMobile={!responsive.isMd}
                setOpen={setOpen}
                onReset={onResetFilter}
                onApply={onApplyFilter}
                mainContainerWidth={700}
                placement={getConditionalContent({
                  condition: responsive?.isMd,
                  trueValue: "bottomRight",
                  falseValue: "bottom",
                })}
                button={
                  <Button
                    icon={
                      <img
                        src="/icons/filter_list.svg"
                        className={Styles["filter-icon-style"]}
                        alt="Filter"
                      />
                    }
                    className={Styles["add-Filter"]}
                    type="text"
                    onClick={() => setOpen(true)}
                  >
                    {getConditionalContent({
                      condition: responsive.isSm,
                      trueValue: "Filters",
                    })}
                    {filtersCount > 0 && (
                      <span
                        style={questionnaireListingTopContainerStyle.countStyle}
                      >
                        {`${filtersCount}`}
                      </span>
                    )}
                  </Button>
                }
              />
              <Button
                icon={
                  <DownloadOutlined className={Styles["plus-icon-style"]} />
                }
                className={`${Styles["add-Filter"]}
                ${
                  authState?.currentRole?.id !== userRole.ADMIN &&
                  Styles["generate-report-ca"]
                } `}
                type="text"
                onClick={downloadQuestionnaireReportsHandler}
              >
                {getConditionalContent({
                  condition: responsive.isSm,
                  trueValue: getLocalizedMessage(intl, "button.generateReport"),
                })}
              </Button>
            </>
          }
          rightSection={
            authState?.currentRole?.id === userRole.ADMIN && (
              <Button
                icon={<PlusOutlined className={Styles["plus-icon-style"]} />}
                className={Styles["add-button"]}
                onClick={() => navigate("/add-questionnaire")}
              >
                {responsive.isSm &&
                  getLocalizedMessage(
                    intl,
                    "questionnaire.addNewQuestionnaire"
                  )}
              </Button>
            )
          }
        />
      }
    />
  );
}

function getCountValue(questionnaireState) {
  let count = 0;
  if (questionnaireState?.query?.prevFilters?.length) {
    for (const obj of questionnaireState?.query?.prevFilters) {
      for (const child of obj?.children) {
        if (child.isSelected) {
          count += 1;
          break;
        }
      }
    }
  }
  return count;
}
